<script setup lang="ts">
import { computed } from 'vue'
import * as da from '@/strings/da'
import { OVERALL_TONE } from '@/lib/presentation'
import type { OverallStatus } from '@/lib/status'

const props = defineProps<{
	status: OverallStatus
	running: boolean
	publicIp: string | null
	phaseLabel: string
	completedSteps: number
	totalSteps: number
	progress: number
	secondsLeft: number
}>()

const copy = computed(() => da.banner[props.status])
const tone = computed(() => OVERALL_TONE[props.status])
</script>

<template>
	<section class="banner" :class="`tone-${tone}`" aria-live="polite">
		<h2 class="headline">{{ running ? phaseLabel : copy.headline }}</h2>
		<p class="body">{{ copy.body }}</p>

		<p class="ip">
			{{ da.firewall.publicIp }}:
			<strong v-if="publicIp">{{ publicIp }}</strong>
			<span v-else-if="running" class="pending">{{ da.firewall.ipPending }}</span>
			<span v-else class="pending">{{ da.firewall.ipUnknown }}</span>
		</p>

		<div v-if="running" class="progress-area">
			<div
				class="track"
				role="progressbar"
				:aria-valuenow="progress"
				aria-valuemin="0"
				aria-valuemax="100"
				:aria-label="da.banner.run.headline"
			>
				<div class="fill" :style="{ width: `${progress}%` }" />
			</div>
			<p class="progress-text">
				{{ da.progress.completed(completedSteps, totalSteps) }} ·
				{{ da.progress.remaining(secondsLeft) }}
			</p>
		</div>
	</section>
</template>

<style scoped>
.banner {
	padding: 18px 20px;
	border: 1px solid var(--tone-border);
	border-left: 4px solid var(--tone-fg);
	border-radius: var(--tcx-radius-sm);
	background: var(--tone-bg);
}

.headline {
	font-size: 1.1875rem;
	font-weight: 700;
	color: var(--tone-fg);
}

.body {
	margin: 6px 0 0;
	max-width: 62ch;
	font-size: 0.9375rem;
	color: var(--tcx-text);
}

.ip {
	margin: 12px 0 0;
	font-size: 0.875rem;
	color: var(--tcx-text);
}

.ip strong {
	font-weight: 500;
	font-variant-numeric: tabular-nums;
}

.pending {
	color: var(--tcx-muted);
}

.progress-area {
	margin-top: 14px;
}

.track {
	height: 6px;
	border-radius: 999px;
	background: rgba(63, 63, 63, 0.1);
	overflow: hidden;
}

.fill {
	height: 100%;
	border-radius: 999px;
	background: var(--tcx-teal-dark);
	transition: width 0.4s ease;
}

.progress-text {
	margin: 8px 0 0;
	font-size: 0.8125rem;
	color: var(--tcx-muted);
	font-variant-numeric: tabular-nums;
}
</style>
